import { useEffect, useRef, useCallback, useState } from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import * as Sentry from '@sentry/react';

/**
 * Custom hook for real-time floorplan collaboration via SignalR.
 * Manages connection to FloorPlanHub and handles live updates for elements,
 * floors, layers, notes, settings, and user presence.
 *
 * @param {number} propertyId - The property ID whose floorplan to subscribe to
 * @param {Object} callbacks - Event callbacks for different floorplan events
 * @param {Function} callbacks.onFloorPlanCreated - Called when a floorplan is created
 * @param {Function} callbacks.onSettingsUpdated - Called when floorplan settings change
 * @param {Function} callbacks.onFloorCreated - Called when a floor is added
 * @param {Function} callbacks.onFloorUpdated - Called when a floor is updated
 * @param {Function} callbacks.onFloorDeleted - Called when a floor is deleted
 * @param {Function} callbacks.onElementsSaved - Called when elements are bulk saved
 * @param {Function} callbacks.onElementDeleted - Called when a single element is deleted
 * @param {Function} callbacks.onLayerUpdated - Called when a layer visibility/opacity changes
 * @param {Function} callbacks.onNoteChanged - Called when a note is added/updated/deleted
 * @param {Function} callbacks.onUserJoined - Called when another user joins the floorplan
 * @param {Function} callbacks.onUserLeft - Called when another user leaves the floorplan
 * @param {Function} callbacks.onActiveUsers - Called with initial list of active users
 * @param {boolean} enabled - Whether the connection should be active
 * @returns {Object} - { isConnected }
 */
const useFloorPlanSignalR = (propertyId, callbacks = {}, enabled = true) => {
    const connectionRef = useRef(null);
    const isConnectedRef = useRef(false);
    const reconnectTimeoutRef = useRef(null);
    const callbacksRef = useRef(callbacks);
    const startConnectionRef = useRef(null);

    // Track connection state in useState so consumers re-render when it changes
    const [isConnected, setIsConnected] = useState(false);

    // Helper to update both the ref (for synchronous internal use) and the state
    const setConnectionState = useCallback((connected) => {
        isConnectedRef.current = connected;
        setIsConnected(connected);
    }, []);

    // Keep callbacks ref up-to-date without triggering reconnection
    useEffect(() => {
        callbacksRef.current = callbacks;
    }, [callbacks]);

    /**
     * Checks whether a JWT token string is expired.
     * JWT uses base64url encoding ("-" and "_" instead of "+" and "/",
     * and no "=" padding). The native `atob` function requires standard
     * base64, so we must convert before decoding to avoid parse errors.
     */
    const isTokenExpired = useCallback((token) => {
        try {
            if (!token) return true;

            // Convert base64url → base64 by replacing chars and re-adding padding
            const base64Url = token.split('.')[1];
            const base64 = base64Url
                .replace(/-/g, '+')
                .replace(/_/g, '/')
                .padEnd(base64Url.length + (4 - (base64Url.length % 4)) % 4, '=');

            const payload = JSON.parse(atob(base64));
            return Date.now() >= payload.exp * 1000;
        } catch (catchError) {
            Sentry.captureException(catchError);
            return true;
        }
    }, []);

    /**
     * Build and start the SignalR connection
     */
    const startConnection = useCallback(async () => {
        if (!enabled || !propertyId || isConnectedRef.current) return;

        try {
            // Read token from the same location AuthContext stores it:
            // localStorage.user is a JSON-serialised user object with a `token` property.
            // Fall back through session storage variants for compatibility.
            const storedUserJson =
                localStorage.getItem('user') ||
                sessionStorage.getItem('user');

            const storedUser = storedUserJson ? JSON.parse(storedUserJson) : null;
            const token = storedUser?.token || null;

            if (!token) {
                console.debug('[SignalR FloorPlan] No access token available');
                return;
            }

            if (isTokenExpired(token)) {
                console.debug('[SignalR FloorPlan] Token expired — skipping connection');
                return;
            }

            // Hub path is /api/hubs/floorplan so that the production Nginx /api/ location
            // block (which carries the WebSocket upgrade headers) correctly forwards the
            // request to the backend.  /hubs/ at the root resolves to the static-file
            // server and returns 404.
            const connection = new HubConnectionBuilder()
                .withUrl('/api/hubs/floorplan', {
                    // Always read the latest token from storage so that refreshed
                    // tokens are used on reconnect without restarting the hook.
                    accessTokenFactory: () => {
                        try {
                            const latestUserJson =
                                localStorage.getItem('user') ||
                                sessionStorage.getItem('user');
                            const latestUser = latestUserJson ? JSON.parse(latestUserJson) : null;
                            return latestUser?.token || token;
                        } catch (catchError) {
                            Sentry.captureException(catchError);
                            return token;
                        }
                    }
                })
                .withAutomaticReconnect({
                    nextRetryDelayInMilliseconds: (retryContext) => {
                        if (retryContext.previousRetryCount === 0) return 0;
                        if (retryContext.previousRetryCount === 1) return 2000;
                        if (retryContext.previousRetryCount === 2) return 10000;
                        if (retryContext.previousRetryCount === 3) return 30000;
                        return 60000;
                    }
                })
                .configureLogging(LogLevel.Information)
                .build();

            // ── Event handlers ──────────────────────────────────────────

            connection.on('FloorPlanCreated', (data) => {
                callbacksRef.current.onFloorPlanCreated?.(data);
            });

            connection.on('SettingsUpdated', (data) => {
                callbacksRef.current.onSettingsUpdated?.(data);
            });

            connection.on('FloorCreated', (data) => {
                callbacksRef.current.onFloorCreated?.(data);
            });

            connection.on('FloorUpdated', (data) => {
                callbacksRef.current.onFloorUpdated?.(data);
            });

            connection.on('FloorDeleted', (data) => {
                callbacksRef.current.onFloorDeleted?.(data);
            });

            connection.on('ElementsSaved', (data) => {
                callbacksRef.current.onElementsSaved?.(data);
            });

            connection.on('ElementDeleted', (data) => {
                callbacksRef.current.onElementDeleted?.(data);
            });

            connection.on('LayerUpdated', (data) => {
                callbacksRef.current.onLayerUpdated?.(data);
            });

            connection.on('NoteChanged', (data) => {
                callbacksRef.current.onNoteChanged?.(data);
            });

            // ── Presence events ─────────────────────────────────────────

            connection.on('ActiveUsers', (data) => {
                callbacksRef.current.onActiveUsers?.(data);
            });

            connection.on('UserJoinedFloorPlan', (data) => {
                callbacksRef.current.onUserJoined?.(data);
            });

            connection.on('UserLeftFloorPlan', (data) => {
                callbacksRef.current.onUserLeft?.(data);
            });

            // ── Reconnection handling ───────────────────────────────────

            connection.onreconnecting(() => {
                console.warn('[SignalR FloorPlan] Reconnecting...');
                setConnectionState(false);
            });

            connection.onreconnected(() => {
                console.log('[SignalR FloorPlan] Reconnected');
                setConnectionState(true);
                connection.invoke('JoinFloorPlan', propertyId).catch(err => {
                    console.error('[SignalR FloorPlan] Error rejoining after reconnect:', err);
                });
            });

            connection.onclose((error) => {
                console.log('[SignalR FloorPlan] Connection closed', error);
                setConnectionState(false);

                if (error && enabled) {
                    const errMsg = error?.message || error?.toString() || '';
                    const isAuthError = errMsg.includes('401') || errMsg.includes('Token expired') || errMsg.includes('Unauthorized');
                    if (!isAuthError) {
                        reconnectTimeoutRef.current = setTimeout(() => {
                            startConnectionRef.current?.();
                        }, 5000);
                    }
                }
            });

            // ── Start ───────────────────────────────────────────────────

            await connection.start();
            console.log('[SignalR FloorPlan] Connected');
            setConnectionState(true);

            await connection.invoke('JoinFloorPlan', propertyId);
            console.log(`[SignalR FloorPlan] Joined floorplan group for property ${propertyId}`);

            connectionRef.current = connection;
        } catch (error) {
            Sentry.captureException(error);
            const errMsg = error?.message || error?.toString() || '';
            const isAuthError = errMsg.includes('401') || errMsg.includes('Token expired') || errMsg.includes('Unauthorized');

            if (isAuthError) {
                console.debug('[SignalR FloorPlan] Connection skipped — session expired');
            } else {
                console.error('[SignalR FloorPlan] Error starting connection:', error);
            }
            setConnectionState(false);

            if (!isAuthError && enabled) {
                reconnectTimeoutRef.current = setTimeout(() => {
                    startConnectionRef.current?.();
                }, 5000);
            }
        }
    }, [propertyId, enabled, isTokenExpired, setConnectionState]);

    /**
     * Stop the SignalR connection
     */
    const stopConnection = useCallback(async () => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (connectionRef.current) {
            try {
                if (isConnectedRef.current && propertyId) {
                    await connectionRef.current.invoke('LeaveFloorPlan', propertyId);
                }
                await connectionRef.current.stop();
                console.log('[SignalR FloorPlan] Connection stopped');
            } catch (error) {
                Sentry.captureException(error);
                console.error('[SignalR FloorPlan] Error stopping connection:', error);
            } finally {
                connectionRef.current = null;
                setConnectionState(false);
            }
        }
    }, [propertyId, setConnectionState]);

    // Keep ref in sync with latest startConnection inside an effect (not during render)
    useEffect(() => {
        startConnectionRef.current = startConnection;
    }, [startConnection]);

    // Start/stop connection when propertyId changes
    useEffect(() => {
        (async () => {
            if (enabled && propertyId) {
                await startConnection();
            }
        })();

        return () => {
            stopConnection();
        };
    }, [propertyId, enabled, startConnection, stopConnection]);

    return {
        isConnected
    };
};

export default useFloorPlanSignalR;
